/**
 * Mirrors the localStorage "selections" format used by the legacy graphFactory.ejs.
 *
 * Format:
 *   localStorage["selections"] = {
 *     "<host><pathname>": { stopnodes: [...], pinnednodes: [...] }
 *   }
 *
 * The key is window.location.host + window.location.pathname, matching
 * what saveSelections() in graphFactory.ejs writes.
 */

const STORAGE_KEY = 'selections'

function storageKey() {
    return window.location.host + window.location.pathname
}

// Re-split on read so comma-glued tokens persisted by older versions
// (e.g. "chi," from ?stopwords=tai+chi,+movement) self-heal without a migration.
const normalizeTokens = arr =>
    (arr || []).flatMap(t => String(t).split(/[\s,]+/)).filter(Boolean)

/**
 * Returns the stop words stored for the current page URL, or [] if none.
 */
export function getStoredStopWords() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const selections = JSON.parse(raw)
        return normalizeTokens(selections[storageKey()]?.stopnodes)
    } catch {
        return []
    }
}

/**
 * Persists stop words for the current page URL.
 * Preserves the pinnednodes field to stay compatible with the legacy format.
 * Pass [] to clear stored stop words for this URL.
 */
export function saveStoredStopWords(stopWords) {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        const selections = raw ? JSON.parse(raw) : {}
        const key = storageKey()
        const existing = selections[key] || { stopnodes: [], pinnednodes: [] }

        if (stopWords.length === 0 && existing.pinnednodes.length === 0) {
            delete selections[key]
        } else {
            selections[key] = { ...existing, stopnodes: stopWords }
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(selections))
    } catch {
        // localStorage unavailable — silently skip
    }
}

/**
 * Returns the pinned nodes stored for the current page URL, or [] if none.
 */
export function getStoredPinnedNodes() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const selections = JSON.parse(raw)
        return normalizeTokens(selections[storageKey()]?.pinnednodes)
    } catch {
        return []
    }
}

/**
 * Persists pinned nodes for the current page URL.
 * Preserves the stopnodes field to stay compatible with the legacy format.
 * Pass [] to clear stored pinned nodes for this URL.
 */
export function saveStoredPinnedNodes(pinnedNodes) {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        const selections = raw ? JSON.parse(raw) : {}
        const key = storageKey()
        const existing = selections[key] || { stopnodes: [], pinnednodes: [] }

        if (pinnedNodes.length === 0 && existing.stopnodes.length === 0) {
            delete selections[key]
        } else {
            selections[key] = { ...existing, pinnednodes: pinnedNodes }
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(selections))
    } catch {
        // localStorage unavailable — silently skip
    }
}

